import { Creator } from "@/types/types";
import { EvePhasePayment, EvePipelinePhase } from "@/types/eve";
import {
  customStatusKind,
  evePayoutState,
  evePhaseKeyForStatus,
  isEvePayoutSlotKey,
} from "@/lib/eve";

/** A prepared payout is one the team can still confirm (preview + unexpired hold). */
export function payoutPrepared(
  payment?: { preview?: unknown; pending_expires_at?: string } | null
): boolean {
  if (!payment?.preview || !payment.pending_expires_at) return false;
  const expires = new Date(payment.pending_expires_at);
  return !isNaN(expires.getTime()) && expires > new Date();
}

export type PhaseStateKind =
  | "done"
  | "not_started"
  | "declined"
  | "sending"
  | "awaiting_signature"
  | "awaiting_claim"
  | "confirm_payout"
  | "preparing"
  | "running"
  | "stalled"
  | "holding"
  | "needs_review"
  | "revision"
  | "waiting_creator";

export const PHASE_STATE_LABELS: Record<PhaseStateKind, string> = {
  done: "Done",
  not_started: "Not started",
  declined: "Declined",
  sending: "Sending",
  awaiting_signature: "Awaiting signature",
  awaiting_claim: "Awaiting claim",
  confirm_payout: "Confirm payout",
  preparing: "Preparing",
  running: "Running",
  stalled: "Stalled",
  holding: "Holding",
  needs_review: "Needs review",
  revision: "In revision",
  waiting_creator: "Waiting on creator",
};

export interface PhaseState {
  kind: PhaseStateKind;
  /** Checklist/filter label — the cell renders its own richer copy per kind. */
  label: string;
  isCurrent: boolean;
  completedEntry?: { key: string; completed_at: string };
  /** Payout slots only: the slot's own subdoc. */
  payoutState?: EvePhasePayment;
}

/**
 * One creator × one pipeline phase → the state PhaseColumnCell draws and the
 * column filter lists. Single source of truth so the checklist can never
 * disagree with the cell.
 */
export function resolvePhaseState(creator: Creator, phase: EvePipelinePhase): PhaseState {
  const status = creator.status ?? "";
  const evePhase = creator.eve_phase;
  const key = phase.key;

  const completedEntry = evePhase?.history?.find((h) => h.key === key);
  const payoutState = isEvePayoutSlotKey(key) ? evePayoutState(evePhase, key) : undefined;
  // Payout columns claim "done" only on payout EVIDENCE (history entry or a
  // confirmed/claimed payout record) — a creator whose run predates a
  // later-added split has top-level status "completed" but never received
  // that payment, and must not read as paid.
  const isCompleted = isEvePayoutSlotKey(key)
    ? !!completedEntry || !!payoutState?.claimed_at || !!payoutState?.confirmed_at
    : !!completedEntry ||
      status === "completed" ||
      (key === "contract" && !!evePhase?.contract?.completed_at);
  const isCurrent = evePhaseKeyForStatus(status) === key;

  const build = (kind: PhaseStateKind): PhaseState => ({
    kind,
    label: PHASE_STATE_LABELS[kind],
    isCurrent,
    completedEntry,
    payoutState,
  });

  if (!isCurrent) return build(isCompleted ? "done" : "not_started");

  if (key === "contract") {
    if (evePhase?.contract?.declined_at) return build("declined");
    if (status === "pending-contract") return build("sending");
    return build("awaiting_signature");
  }
  if (isEvePayoutSlotKey(key)) {
    if (status.startsWith("waiting-")) return build("awaiting_claim");
    if (payoutPrepared(payoutState)) return build("confirm_payout");
    return build("preparing");
  }
  if (customStatusKind(status) === "run") return build("running");
  if (customStatusKind(status) === "wait") {
    return build(evePhase?.gate?.stall_announced_at ? "stalled" : "holding");
  }
  if (status.endsWith("-review")) return build("needs_review");
  return build((evePhase?.revision_count ?? 0) > 0 ? "revision" : "waiting_creator");
}
